import { useEffect, useState } from "react";
import { AdminBookingAPI } from "../../../services/adminBooking.api";

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
};

export default function BookingTable({ booking, onAction }) {
  if (!booking) return null;

  const safeStatus = booking.paymentStatus || "PENDING";

  const [timeLeft, setTimeLeft] = useState(null);

  const isUrgent =
    safeStatus === "PENDING" &&
    booking.expiresAt &&
    new Date(booking.expiresAt) - new Date() < 5 * 60 * 1000;

  useEffect(() => {
    if (!booking.expiresAt) return;

    const interval = setInterval(() => {
      const diff = new Date(booking.expiresAt) - new Date();

      if (diff <= 0) {
        setTimeLeft("Expired");
        clearInterval(interval);
      } else {
        const mins = Math.floor(diff / 60000);
        setTimeLeft(`${mins} min`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [booking.expiresAt]);

  const ticketCount = booking.ticketCount ?? booking.ticket?.quantity ?? 0;
  const redeemedCount = booking.redeemedCount ?? 0;
  const redeemStatus = booking.redeemStatus ?? "none";
  const tierName = booking.ticket?.tierName || "—";

  /* ================= REDEEM BADGE ================= */
  function RedeemBadge() {
    if (safeStatus !== "PAID" || ticketCount === 0) {
      return <span className="redeem-badge redeem-badge--na">—</span>;
    }

    if (redeemStatus === "all") {
      return (
        <span className="redeem-badge redeem-badge--all">
          ✓ All Redeemed ({redeemedCount}/{ticketCount})
        </span>
      );
    }

    if (redeemStatus === "partial") {
      return (
        <span className="redeem-badge redeem-badge--partial">
          ◐ Partial ({redeemedCount}/{ticketCount})
        </span>
      );
    }

    // none
    return (
      <span className="redeem-badge redeem-badge--none">
        Not Redeemed ({redeemedCount}/{ticketCount})
      </span>
    );
  }

  /* ================= RESEND TICKET ================= */
  const [resending, setResending] = useState(false);
  const [resendOk, setResendOk] = useState(false);

  async function handleResend(e) {
    e.stopPropagation();
    if (resending || resendOk) return;
    setResending(true);
    try {
      await AdminBookingAPI.resendTicket(booking._id);
      setResendOk(true);
      setTimeout(() => setResendOk(false), 3000);
    } catch {
      alert("Gagal kirim ulang tiket");
    } finally {
      setResending(false);
    }
  }

  /* ================= VERIFY / REJECT ================= */
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  /* ================= RESCUE ================= */
  const [rescuing, setRescuing] = useState(false);

  async function handleVerify(e) {
    e.stopPropagation();
    if (verifying) return;
    if (!window.confirm("Verifikasi pembayaran ini?")) return;
    setVerifying(true);
    try {
      await AdminBookingAPI.verify(booking._id);
      onAction?.();
    } catch {
      alert("Gagal verifikasi");
    } finally {
      setVerifying(false);
    }
  }

  async function handleReject(e) {
    e.stopPropagation();
    if (rejecting) return;
    setRejecting(true);
    try {
      await AdminBookingAPI.reject(booking._id, { reason: rejectReason || undefined });
      onAction?.();
    } catch {
      alert("Gagal reject");
    } finally {
      setRejecting(false);
      setShowRejectInput(false);
      setRejectReason("");
    }
  }

  async function handleRescue(e) {
    e.stopPropagation();
    if (rescuing) return;
    const nama = booking.customerSnapshot?.name || "pelanggan ini";
    const total = booking.ticket?.total?.toLocaleString("id-ID") || "?";
    if (!window.confirm(
      `Pulihkan booking milik ${nama} (Rp ${total})?\n\nPastikan pembayaran QRIS benar-benar diterima sebelum melanjutkan.`
    )) return;
    setRescuing(true);
    try {
      await AdminBookingAPI.rescue(booking._id);
      onAction?.();
    } catch {
      alert("Gagal memulihkan booking");
    } finally {
      setRescuing(false);
    }
  }

  /* ================= TICKET CODES (expandable) ================= */
  const [showCodes, setShowCodes] = useState(false);
  const codes = booking.ticketCodes || [];
  const hasCodes = codes.length > 0;

  /* ================= WA MESSAGE ================= */
  function buildWaMessage() {
    const origin = window.location.origin;
    const name = (booking.customerSnapshot?.name || "")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
    const tier = tierName !== "—" ? ` (${tierName})` : "";

    if (ticketCount === 1) {
      const code = codes[0]?.code || booking._id;
      return `Halo ${name}, berikut tiket kamu:\n\n1 Tiket${tier}\nKode: ${code}\n\nKlik untuk lihat & download tiket:\n${origin}/booking/${booking._id}/ticket`;
    }

    // Multi-ticket: list all codes compactly
    const codeLines = codes.map((t, i) => `Tiket ${i + 1}: ${t.code}`).join("\n");
    return `Halo ${name}, berikut tiket kamu:\n\n${ticketCount} Tiket${tier}\n${codeLines}\n\nKlik untuk lihat & download semua tiket:\n${origin}/booking/${booking._id}/ticket`;
  }

  const waMessage = buildWaMessage();
  const waUrl = booking.customerSnapshot?.phone
    ? `https://wa.me/${booking.customerSnapshot.phone.replace(/^0/, "62")}?text=${encodeURIComponent(waMessage)}`
    : null;

  return (
    <div
      className={`booking-row ${safeStatus.toLowerCase()} ${isUrgent ? "urgent" : ""}`}
    >
      {/* ================= INFO ================= */}
      <div className="info-main">
        <strong>{booking.customerSnapshot?.name}</strong>
        <span>{booking.customerSnapshot?.email}</span>
        <span className="booking-date">🗓 {fmtDate(booking.createdAt)}</span>
        {booking.customerSnapshot?.phone && (
          <span>📱 {booking.customerSnapshot.phone}</span>
        )}
        {timeLeft && safeStatus === "PENDING" && (
          <span className="expire">⏳ {timeLeft}</span>
        )}
      </div>

      {/* ================= EVENT ================= */}
      <div className="event-info">
        <span className="event-name">
          {booking.event?.title || "—"}
        </span>
        {ticketCount > 0 && (
          <span className="ticket-count">
            🎟️ {ticketCount} Tiket
            {tierName !== "—" && ` (${tierName})`}
          </span>
        )}
        {hasCodes && (
          <button
            className="codes-toggle"
            onClick={(e) => { e.stopPropagation(); setShowCodes(s => !s); }}
          >
            {showCodes ? "▲ Sembunyikan" : "▼ Lihat kode"}
          </button>
        )}
        {showCodes && hasCodes && (
          <div className="ticket-codes-list">
            {codes.map((t, i) => (
              <span key={t.code || i} className="ticket-code-item">
                <span className="code-num">{i + 1}.</span>
                <span className="code-val">{t.code}</span>
                {t.status === "used" && (
                  <span className="code-redeemed">✓ Redeemed</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAYMENT ================= */}
      <div className="payment-info">
        <div className="amount">
          Rp {booking.ticket?.total?.toLocaleString("id-ID")}
        </div>
        <div className="method">
          {`${booking.payment?.method || "—"} - ${booking.payment?.provider || "—"}`}
          {booking.payment?.rrn && (
            <small>RRN: {booking.payment.rrn}</small>
          )}
        </div>
      </div>

      {/* ================= STATUS ================= */}
      <div className="status">
        <span className={`pill ${safeStatus.toLowerCase()}`}>
          {safeStatus}
        </span>
        <RedeemBadge />
      </div>

      {/* ================= ACTIONS ================= */}
      <div className="actions">
        {safeStatus === "EXPIRED" && (
          <button
            className="btn rescue"
            onClick={handleRescue}
            disabled={rescuing}
            title="Pulihkan booking ini ke status PAID (untuk pembayaran yang berhasil tapi terlambat)"
          >
            {rescuing ? "..." : "Pulihkan"}
          </button>
        )}

        {safeStatus === "PENDING" && (
          <>
            <button
              className="btn verify"
              onClick={handleVerify}
              disabled={verifying}
            >
              {verifying ? "..." : "Verify"}
            </button>

            {!showRejectInput ? (
              <button
                className="btn reject"
                onClick={(e) => { e.stopPropagation(); setShowRejectInput(true); }}
              >
                Reject
              </button>
            ) : (
              <div className="reject-input" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  placeholder="Alasan (opsional)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleReject(e)}
                  autoFocus
                />
                <button
                  className="btn reject"
                  onClick={handleReject}
                  disabled={rejecting}
                >
                  {rejecting ? "..." : "Konfirmasi"}
                </button>
                <button
                  className="btn"
                  onClick={(e) => { e.stopPropagation(); setShowRejectInput(false); setRejectReason(""); }}
                >
                  Batal
                </button>
              </div>
            )}
          </>
        )}

        {safeStatus === "PAID" && (
          <>
            {waUrl && (
              <a
                className="btn wa"
                target="_blank"
                rel="noreferrer"
                href={waUrl}
              >
                WA
              </a>
            )}

            <a
              className="btn download"
              href={`${window.location.origin}/booking/${booking._id}/ticket`}
              target="_blank"
              rel="noreferrer"
            >
              Download
            </a>

            <button
              className={`btn resend ${resendOk ? "resend--ok" : ""}`}
              onClick={handleResend}
              disabled={resending || resendOk}
            >
              {resendOk ? "✓ Terkirim" : resending ? "..." : "Resend"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}